import React, { useState } from 'react';
/**
 * AuthGuard — Authentication + Onboarding screen rendering
 *
 * Extracted from App.tsx in R34 (DDD Fase 6 — App Shell decomposition).
 * Renders WelcomeScreen, LoginScreen, or RegisterScreen when the user
 * is not authenticated.
 *
 * Fase 2 addition (s87): After authentication, checks hasCompletedOnboarding.
 * If false/undefined, shows role-appropriate onboarding:
 * - Learner → JoinClassroomPrompt (enter classroom joinCode)
 * - Parent → LinkChildPrompt (enter child's linkCode)
 * - Teacher → CreateFirstClassroom (create first class)
 * - Admin → skip (no onboarding needed)
 */
import { WelcomeScreen, LoginScreen, RegisterScreen, OnboardingWizard, type AccountsState, type Account } from '../accounts';
interface AuthGuardProps {
  showWelcome: boolean;
  currentNavView: string;
  accounts: AccountsState;
  activeAccount: Account | null;
  onLoadDemo: () => void;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onLoginSuccess: (accountId: string) => void;
  onBack: () => void;
  createAccount: (role: 'learner' | 'teacher' | 'parent' | 'admin', details: {
    displayName: string;
    email?: string;
    schoolName?: string;
    grade?: number;
    passwordHash?: string;
    username?: string;
    linkCode?: string;
  }, options?: {
    activate?: boolean;
    cleanupDemo?: boolean;
  }) => string;
  // Onboarding callbacks
  onJoinClassroom: (joinCode: string) => {
    success: boolean;
    error?: string;
  };
  onLinkChild: (linkCode: string) => {
    success: boolean;
    error?: string;
  };
  onCreateClassroom: (name: string, grade: number) => void;
  onJoinExistingClassroom?: (classroomId: string, mode: 'transfer' | 'coteach') => void;
  onCompleteOnboarding: () => void;
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToMyClass?: () => void;
  children: React.ReactNode;
}
export function AuthGuard({
  showWelcome,
  currentNavView,
  accounts,
  activeAccount,
  onLoadDemo,
  onNavigateToLogin,
  onNavigateToRegister,
  onLoginSuccess,
  onBack,
  createAccount,
  onJoinClassroom,
  onLinkChild,
  onCreateClassroom,
  onJoinExistingClassroom,
  onCompleteOnboarding,
  onNavigateToModule,
  onNavigateToMyClass,
  children
}: AuthGuardProps): React.ReactElement {
  const [onboardingError, setOnboardingError] = useState<string | undefined>();
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  // ── Pre-auth screens ───────────────────────────────────────
  if (showWelcome) {
    return <WelcomeScreen onLoadDemo={onLoadDemo} onLogin={onNavigateToLogin} onRegister={onNavigateToRegister} data-id="element-774" />;
  }
  if (currentNavView === 'login') {
    return <LoginScreen onLoginSuccess={onLoginSuccess} onBack={onBack} onNavigateToRegister={onNavigateToRegister} accounts={accounts} data-id="element-775" />;
  }
  if (currentNavView === 'register') {
    return <RegisterScreen onRegisterSuccess={onLoginSuccess} onBack={onBack} onNavigateToLogin={onNavigateToLogin} accounts={accounts} createAccount={createAccount} data-id="element-776" />;
  }
  // ── Post-auth onboarding ───────────────────────────────────
  // Show onboarding for newly registered accounts that haven't completed it.
  // Demo accounts and admins skip onboarding entirely.
  if (activeAccount && !activeAccount.hasCompletedOnboarding && !activeAccount.isDemo && activeAccount.role !== 'admin') {
    return <OnboardingWizard account={activeAccount} accounts={accounts} onJoinClassroom={onJoinClassroom} onLinkChild={onLinkChild} onCreateClassroom={onCreateClassroom} onJoinExistingClassroom={onJoinExistingClassroom} onCompleteOnboarding={onCompleteOnboarding} onNavigateToModule={onNavigateToModule} onNavigateToMyClass={onNavigateToMyClass} data-id="element-777" />;
  }
  return <>{children}</>;
}